import React, { useEffect, useState } from "react";
import api from "../../../../../utils/axiosInstance";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";

const EditLanguagePairsPage = ({ vendorId, onUpdateComplete }) => {
  const [languagePairs, setLanguagePairs] = useState([]);
  const [selectedPairs, setSelectedPairs] = useState(new Set());
  const [initialSelection, setInitialSelection] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const { data: res } = await api.get(
          `/vendor-language-pairs/${vendorId}/admin-pairs`,
          { withCredentials: true }
        );

        const pairs = res?.data || [];
        setLanguagePairs(pairs);

        const selected = new Set(
          pairs.filter((p) => p.is_selected).map((p) => p.id)
        );
        setSelectedPairs(selected);
        setInitialSelection(selected);
        setError("");
      } catch (error) {
        console.error("Error fetching language pairs:", error);
        setError("Failed to load language pairs");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);

  const handleCheckboxChange = (pairId) => {
    setSelectedPairs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pairId)) {
        newSet.delete(pairId);
      } else {
        newSet.add(pairId);
      }
      return newSet;
    });
  };

  const persistChanges = async (toAdd, toRemove) => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      if (toAdd.length > 0) {
        await api.post(
          "/vendor-language-pairs/bulk-create",
          { vendor_id: vendorId, language_pair_ids: toAdd },
          { withCredentials: true }
        );
      }

      if (toRemove.length > 0) {
        await api.post(
          "/vendor-language-pairs/bulk-delete",
          { vendor_id: vendorId, language_pair_ids: toRemove },
          { withCredentials: true }
        );
      }

      setSuccessMessage("Language pairs updated successfully!");
      setTimeout(() => {
        if (typeof onUpdateComplete === "function") {
          onUpdateComplete();
        }
      }, 1500);
    } catch (error) {
      console.error("Error saving language pairs:", error);
      setError("Failed to update language pairs. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const currentSelection = Array.from(selectedPairs);
    const previousSelection = Array.from(initialSelection);

    const toAdd = currentSelection.filter((id) => !previousSelection.includes(id));
    const toRemove = previousSelection.filter((id) => !currentSelection.includes(id));

    if (toRemove.length > 0) {
      const pairsToRemove = languagePairs.filter((p) => toRemove.includes(p.id));
      const totalPrices = pairsToRemove.reduce((sum, p) => sum + (p.price_count || 0), 0);

      if (totalPrices > 0) {
        setConfirm({
          title: "Remove Language Pairs?",
          message: `This will delete ${totalPrices} price${totalPrices === 1 ? "" : "s"} from ${toRemove.length} language pair${toRemove.length === 1 ? "" : "s"}. Continue?`,
          onConfirm: () => {
            setConfirm(null);
            persistChanges(toAdd, toRemove);
          },
        });
        return;
      }
    }

    persistChanges(toAdd, toRemove);
  };

  const handleSelectAll = () => {
    setSelectedPairs(new Set(languagePairs.map((p) => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedPairs(new Set());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <p className="text-gray-500 text-sm animate-pulse">Loading language pairs...</p>
      </div>
    );
  }

  return (
    <div>
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 rounded mb-3">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded mb-3 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="text-red-900 hover:text-red-700 font-bold ml-2"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{selectedPairs.size}</span> of{" "}
          <span className="font-semibold text-gray-900">{languagePairs.length}</span> selected
        </div>
        <div className="flex gap-2 text-sm">
          <button
            onClick={handleSelectAll}
            disabled={saving}
            className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            disabled={saving}
            className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            Deselect All
          </button>
        </div>
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                <th className="px-3 py-2 text-center font-semibold w-12">Select</th>
                <th className="px-3 py-2 text-center font-semibold">Source Language</th>
                <th className="px-3 py-2 text-center font-semibold">Target Language</th>
                <th className="px-3 py-2 text-center font-semibold w-20">Prices</th>
              </tr>
            </thead>
            <tbody>
              {languagePairs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-400 text-xs">
                    No language pairs available.
                  </td>
                </tr>
              ) : (
                languagePairs.map((pair, index) => {
                  const isSelected = selectedPairs.has(pair.id);
                  const rowBg = isSelected
                    ? "bg-blue-50"
                    : index % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white";
                  return (
                    <tr
                      key={pair.id}
                      className={`${rowBg} hover:bg-blue-50 transition-colors cursor-pointer`}
                      onClick={() => !saving && handleCheckboxChange(pair.id)}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(pair.id)}
                          disabled={saving}
                          className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-900">
                        {pair.sourceLanguage?.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-900">
                        {pair.targetLanguage?.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-700">
                        {pair.price_count || 0}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex mt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-3 py-1.5 text-sm font-medium rounded text-white transition ${
            saving
              ? "bg-blue-300 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
          confirmText="Continue"
        />
      )}
    </div>
  );
};

export default EditLanguagePairsPage;
